import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme';

export interface StudentGroupBadgeRowProps extends HTMLAttributes<HTMLElement> {
  /** Grupos do utilizador — um chip por grupo (lado a lado, com wrap). */
  groups: Array<{ id: string; name: string }>;
  hint: ReactNode;
  /** Enquanto os dados do utilizador estão a carregar. */
  loading?: boolean;
}

export function StudentGroupBadgeRow({
  groups,
  hint,
  loading = false,
  style,
  ...rest
}: StudentGroupBadgeRowProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const pill = theme.palette.studentDashboard.groupPill;
  const L = theme.layout.studentDashboard;

  const chipStyle = {
    borderRadius: theme.shape.borderRadius.sm2,
    padding: `${theme.spacing.xs} ${theme.spacing.sm2}`,
    border: `${theme.shape.borderWidth.thin} solid ${pill.border}`,
    color: pill.text,
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.medium,
    backgroundColor: pill.background,
  } as const;

  return (
    <section
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing.sm,
        ...style,
      }}
      {...rest}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: theme.spacing.xs,
          minWidth: 0,
        }}
      >
        {loading ? (
          <span style={chipStyle} aria-busy="true">
            {t('student_dashboard.group_chips_loading')}
          </span>
        ) : (
          groups.map(g => (
            <span key={g.id} style={chipStyle}>
              {g.name}
            </span>
          ))
        )}
      </div>
      <span
        style={{
          color: theme.palette.text.secondary,
          fontSize: theme.typography.fontSize.base,
          fontWeight: theme.typography.fontWeight.normal,
          flex: `1 1 ${L.groupHintFlexBasis}`,
          minWidth: 0,
        }}
      >
        {hint}
      </span>
    </section>
  );
}
